/**
 * Signature block expansion
 * ──────────────────────────
 * Expands a generic `SignatureBlockDefinition` into a borderless table of
 * `DocumentChild` nodes (one column per party). Shared by BOTH renderers so
 * the visual layout stays identical, and so anchor-token emission is defined
 * in one place. Field values / anchors stay as token strings - each renderer
 * substitutes them at render time.
 *
 * `mode`:
 *   - 'visual'         → ruled lines + captions, no anchor tokens
 *   - 'anchors'        → ruled lines + captions + e-sign anchor tokens
 *   - 'visual+anchors' → same as 'anchors'
 */
import { evenColumnWidths } from './schema/contract.js';
const DEFAULT_FIELD_LABELS = {
    company: 'Company',
    name: 'Name',
    title: 'Title',
    signature: 'Signature',
    date: 'Date',
};
const RULE_LINE = '____________________________';
export function expandSignatureBlock(def) {
    const mode = def.mode ?? 'visual';
    const includeAnchors = mode !== 'visual';
    const labels = { ...DEFAULT_FIELD_LABELS, ...(def.fieldLabels ?? {}) };
    const cells = def.parties.map((party) => {
        const paragraphs = [];
        if (party.label) {
            paragraphs.push({
                type: 'paragraph',
                children: [{ text: party.label, bold: true }],
                spacing: { after: 80 },
            });
        }
        for (const field of party.fields) {
            const value = field !== 'signature'
                ? party.values?.[field]
                : undefined;
            const anchor = includeAnchors && field !== 'company'
                ? party.anchors?.[field]
                : undefined;
            const lineText = value ?? anchor ?? '';
            // The value (or anchor) sits on the ruled line.
            paragraphs.push({
                type: 'paragraph',
                children: [{ text: lineText, bold: field === 'company' }],
                spacing: { before: 160, after: 0 },
            });
            // The ruled line itself.
            paragraphs.push({
                type: 'paragraph',
                children: [{ text: RULE_LINE, color: '94A3B8' }],
                spacing: { after: 0 },
            });
            // The field caption.
            paragraphs.push({
                type: 'paragraph',
                children: [{ text: labels[field] ?? field, size: 16, color: '64748B' }],
                spacing: { after: 80 },
            });
        }
        return {
            children: paragraphs,
            margins: { top: 80, bottom: 80, left: 80, right: 240 },
        };
    });
    const colCount = def.parties.length || 1;
    const columnWidths = evenColumnWidths(colCount);
    return [
        {
            type: 'table',
            rows: [{ children: cells }],
            width: { size: 100, type: 'pct' },
            columnWidths,
            borders: false,
        },
    ];
}
//# sourceMappingURL=signature.js.map